import { EnvironmentId } from "@cmmd-forge/contracts";

import type { RemoteRuntimeConnectionState } from "../components/chat/remoteRuntimeThreadConnection.logic";
import type { RemoteThreadExecutionIntentState } from "../components/chat/useRemoteThreadExecutionIntent";
import { findRemoteRuntimeThreadRouteTarget } from "../components/chat/remoteRuntimeThreadRoute.logic";
import type { CmmdRuntimeThreadReplicaSnapshot } from "../cmmd/cmmdRuntimeThreadReplicaResource";

export type RemoteRuntimeThreadTarget = ReturnType<typeof findRemoteRuntimeThreadRouteTarget>;

export function applyExecutionIntentConnectionState(
  observed: RemoteRuntimeConnectionState,
  executionIntent: RemoteThreadExecutionIntentState,
  options: {
    readonly hasObservedLiveConnection?: boolean;
    readonly hasStagedSubmission?: boolean;
  } = {},
): RemoteRuntimeConnectionState {
  if (observed.kind === "live") return observed;
  if (executionIntent.kind === "failed") return executionIntent;
  if (
    executionIntent.kind === "ready" &&
    options.hasObservedLiveConnection &&
    options.hasStagedSubmission
  ) {
    return { kind: "live" };
  }
  if (
    executionIntent.kind === "connecting" ||
    executionIntent.kind === "updating" ||
    executionIntent.kind === "ready"
  ) {
    return { kind: "connecting" };
  }
  return observed;
}

export function resolveRuntimeAwareThreadConnectionState(input: {
  readonly handoffError: string | null;
  readonly observed: RemoteRuntimeConnectionState;
  readonly executionIntent: RemoteThreadExecutionIntentState;
  readonly retainedLocalSurface: {
    readonly hasObservedLiveConnection: boolean;
    readonly hasStagedSubmission: boolean;
  };
}): RemoteRuntimeConnectionState {
  return input.handoffError === null
    ? applyExecutionIntentConnectionState(
        input.observed,
        input.executionIntent,
        input.retainedLocalSurface,
      )
    : { kind: "failed", message: input.handoffError };
}

export function cloudTargetEnvironmentId(
  target: RemoteRuntimeThreadTarget,
  environmentId: string,
): EnvironmentId | null {
  return target?.connectionKind === "cloud" ? EnvironmentId.make(environmentId) : null;
}

export function relayRuntimeTarget(target: RemoteRuntimeThreadTarget) {
  return target?.connectionKind === "relay" ? target : null;
}

export function executionIntentRuntimeId(
  target: RemoteRuntimeThreadTarget,
  environmentId: string,
): string {
  return target?.runtimeId ?? environmentId;
}

export function remoteThreadHandoffHistoryReady(
  connectionKind: NonNullable<RemoteRuntimeThreadTarget>["connectionKind"] | null,
  replicaKind: CmmdRuntimeThreadReplicaSnapshot["kind"],
): boolean {
  return connectionKind !== "cloud" || replicaKind === "ready";
}
